import {ApiError, ICartPosition} from "@connox/typescript-library";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import ItemList from "../../../cart/ItemList/ItemList";
import VoucherBlock from "../../../cart/Rebates/VoucherBlock";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {INTERNAL_ERROR_TYPE, PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, CheckoutStep, DEFAULT_ERROR_MESSAGE} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import AnnotationBlock from "./AnnotationBlock";
import SummaryAddress from "./SummaryAddress";
import SummaryNewsletter from "./SummaryNewsletter";
import SummaryPaymentOption from "./SummaryPaymentOption";
import SummaryShippingOption from "./SummaryShippingOption";
import SummaryCustomerAccount from "./SummaryCustomerAccount";
import braintree, {BraintreeError} from "braintree-web";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import styles from "./Summary.module.scss";
import {isSameAddress} from "../AddressStep/AddressStep";
import {BraintreeErrorTypes} from "braintree-web/core";
import {authReplacer, captureSentryException} from "../../../../helpers/sentryHelper";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";
import BraintreeButtonLoader from "../../../lib/payments/BraintreeButtonLoader";
import {purchase} from "../../../../helpers/purchase";
import {ErrorPlaceInfoType} from "../../../../helpers/paypal/onErrorClosure";
import {
  ThreeDSecureInfo,
  ThreeDSecureVerificationData,
} from "braintree-web/three-d-secure";
import {getVerifyCardOptions, GPayAuthError} from "../../../../helpers/googlepay/onGoogleAuthorized";

const Summary: React.FC = () => {
  const {setIsIntoCheckout, viewState, braintreeToken, setViewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const checkoutView = useCheckoutViewContext();
  const {cartData, paymentId} = checkout;
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(
    paymentId ? PaymentController.createPayment(checkout, paymentId) : null,
  );
  const {i18n, t} = useTranslation(["errorcodes", "translations"]);
  const itemPosCount = cartData.positions.filter((pos: ICartPosition) => pos.type === "Item" && !!pos.item).length;
  const buttonText = t("checkout.purchaseNow");
  const [errors, setErrors] = useState<(string | JSX.Element)[]>([]);
  const previousValuesRef = useRef<{cartAmount: string; voucherPosition?: ICartPosition}>({
    cartAmount: cartData.value.payable.amount,
    voucherPosition: cartData.positions.find((pos) => pos.type === "Voucher"),
  });
  const restrictingPayments = [Payments.PaypalExpressBraintree, Payments.CreditCardBraintree, Payments.GooglePay];
  const isCartManipulationAllowed = paymentId && !restrictingPayments.includes(paymentId);

  const purchaseButtonErrors = (
    err: ApiError | BraintreeError | Error,
    occurence: string,
    additional?: Record<string, unknown>,
  ) => {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }

    const BTOrDefaultError: Error | IBraintreeError = ((err as ApiError).originalError || err) as Error;

    let errorMessage: string[] = [BTOrDefaultError.message];

    const braintreeOrNetworkError = BTOrDefaultError as IBraintreeError & PossibleNetworkError;

    /* It's possibly a network error if BraintreeError.type does not exist */
    if (!braintreeOrNetworkError.type) {
      const networkErrorMessages = braintreeOrNetworkError.response?.data?.messages;
      if (networkErrorMessages && networkErrorMessages.length) {
        errorMessage = networkErrorMessages.map((msg) => msg.message);
      }
    } else if ("details" in braintreeOrNetworkError) {
      try {
        errorMessage =
          braintreeOrNetworkError.details.originalError.details.originalError?.error.message.split("\u000A") ??
          errorMessage;
      } catch (e) {
        errorMessage = braintreeOrNetworkError.details?.originalError?.error?.message.split("\u000A") ?? errorMessage;
      }
      if (
        errorMessage.length > 1 &&
        errorMessage.includes("Billing line1 format is invalid.") &&
        errorMessage.includes("Shipping line1 format is invalid.") &&
        isSameAddress(checkout.invoiceAddress, checkout.deliveryAddress)
      ) {
        errorMessage.splice(errorMessage.indexOf("Billing line1 format is invalid."), 1);
        errorMessage.splice(errorMessage.indexOf("Shipping line1 format is invalid."), 1);
        errorMessage.push("Shipping/Billing line1 format is invalid.");
      }
    }
    /* else, just keep "errorMessage = [BTOrDefaultError.message]", as stated above */

    const {knownMessages, unknownMessages} = errorMessage.reduce<{knownMessages: string[]; unknownMessages: string[]}>(
      (acc, curr) => {
        if (i18n.exists(curr, {ns: "errorcodes"})) {
          acc.knownMessages.push(curr);
        } else {
          acc.unknownMessages.push(authReplacer(curr));
        }

        return acc;
      },
      {knownMessages: [], unknownMessages: []},
    );

    // if any error is unknown show a default error msg and capture a sentry exception
    if (unknownMessages.length) {
      BTOrDefaultError.name = (err as Error).name;
      const sentryEventId = captureSentryException(BTOrDefaultError, checkout, occurence || "Summary Purchase Button", {
        ...additional,
        knownMessages,
        unknownMessages,
      });

      // just put default msg to the first index
      unknownMessages.unshift(t(DEFAULT_ERROR_MESSAGE, {sentryEventId}));
      // and delete the others, since it won't result in anything the customer can deal with
      unknownMessages.splice(1);
    }

    setErrors([...knownMessages.map((msg) => t(msg)), ...unknownMessages]);
  };

  /**
   * set payment module according to payment id if set
   */
  useEffect(() => {
    if (paymentId === Payments.PaypalExpressBraintree) {
      return;
    }
    if (paymentId) {
      setPaymentModule(PaymentController.createPayment(checkout, paymentId));
    }
  }, [paymentId]);

  /**
   * do initial things when view is visible and payment module is set
   */
  useEffect(() => {
    // If either one is not set, something is wrong here
    if (!(paymentModule && paymentId)) {
      return;
    }

    // Nothing to then but only to catch
    void paymentModule.onEnterSummary().catch((err: Error) => {
      err.name = INTERNAL_ERROR_TYPE.PAYPAL.OnCatch + err.name;
      purchaseButtonErrors(err, "on Enter Summary");
    });
  }, [paymentModule]);

  /**
   * re-init on-enter-summary procedure on cart value change or voucher change
   */
  useEffect(() => {
    const {cartAmount, voucherPosition} = previousValuesRef.current;
    const newCartAmount = cartData.value.payable.amount;
    const newVoucherPos = cartData.positions.find((pos) => pos.type === "Voucher" && pos.reference?.voucher);

    const amountChanged = cartAmount !== newCartAmount;
    const voucherChanged = voucherPosition !== newVoucherPos;
    const isPaypal = paymentId === Payments.PaypalBraintree;

    if ((amountChanged || voucherChanged) && cartData.isValid && isPaypal && paymentModule) {
      setErrors([]);
      void paymentModule.onEnterSummary().catch((err: Error) => {
        err.name = INTERNAL_ERROR_TYPE.PAYPAL.OnReEnter + err.name;
        purchaseButtonErrors(err, "on cart change in Summary");

        previousValuesRef.current = {
          cartAmount: newCartAmount,
          voucherPosition: newVoucherPos,
        };
      });
    }
  }, [cartData]);

  // region insanely large button logic

  // TODO evtl ein bisschen aufräumen
  const GooglePayPurchaseButton: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);
    const onPurchase = async () => {
      const errName = INTERNAL_ERROR_TYPE.GOOGLE_PAY.OnPurchase + "Error";
      const occurrence = "Google Pay Summary Button";
      if (!viewState.gPayData) {
        setIsIntoCheckout(false);

        return;
      }

      setSubmitted(true);

      try {
        // 'network-' vs. 'non-network-tokenized' payment data.
        // See <https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side/javascript/v3/#using-3d-secure-with-google-pay>
        if (viewState.gPayData.tokenData.details.isNetworkTokenized) {
          await purchase(checkout, viewState.gPayData.tokenData.nonce, "gpay");
        } else {
          if (!braintreeToken) {
            const noBtTokenError = new Error();
            noBtTokenError.name = INTERNAL_ERROR_TYPE.GOOGLE_PAY.OnPurchase + "Error";
            purchaseButtonErrors(
              new GPayAuthError("No braintreeToken given in Summary with GPay set", errName),
              occurrence,
            );

            return;
          }

          // const {client, token} = await PaymentController.initBraintreeClient();
          const client = await braintree.client.create({authorization: braintreeToken});
          const threeDSecure = await braintree.threeDSecure.create({client, authorization: braintreeToken, version: 2});

          threeDSecure.on("lookup-complete", (data: ThreeDSecureVerificationData, next) => {
            // INFO: This is all before the opening of the popup
            if (!data) {
              throw new GPayAuthError("No lookup-complete data before 3DSecure popup opening", errName);
            }
            const status = (data.paymentMethod.threeDSecureInfo as ThreeDSecureInfo & {status: string}).status;
            if (["authentication_unavailable", "unsupported_card"].includes(status)) {
              throw new Error("Invalid Credit Card Number");
            }
            if (!data.threeDSecureInfo.liabilityShiftPossible) {
              // this message is a known key in the `errorcodes.json`
              throw new Error("Invalid Credit Card Number");
            }
            if (typeof next !== "function") {
              throw new GPayAuthError("No `next()` method given in 'lookup-complete' callback", errName);
            }

            // this will open the popup
            next();
          });

          let wasCancelledByUser = false;
          threeDSecure.on("customer-canceled", () => {
            wasCancelledByUser = true;
          });
          // eslint-disable-next-line no-console
          console.log("gpay paymentData before 3DS verify:", viewState.gPayData.paymentData);

          const threeDSVerifyPayload = await threeDSecure.verifyCard(
            getVerifyCardOptions(viewState.gPayData, checkout.cartData.value.payable.amount),
          );

          if (!threeDSVerifyPayload.threeDSecureInfo.liabilityShifted) {
            setSubmitted(false);
            setViewState({...viewState, gPayData: undefined});
            if (wasCancelledByUser) {
              setIsIntoCheckout(false);
              updateUrlParams("cart");
            } else {
              purchaseButtonErrors(new GPayAuthError("Liability not shifted", errName), "Google Pay Summary Button", {
                threeDSecureInfo: threeDSVerifyPayload.threeDSecureInfo,
              });
            }

            return;
          }

          await purchase(checkout, threeDSVerifyPayload.nonce, "gpay3ds");
        }
      } catch (err) {
        purchaseButtonErrors(err as Error, occurrence);
      }
    };

    return (
      <Button
        action={onPurchase}
        styles={[ButtonStyles.wide]}
        type={submitted ? ButtonType.disabled : ButtonType.default}
      >
        {submitted ? <Spinner /> : buttonText}
      </Button>
    );
  };

  /* PAYPAL EXPRESS */
  const PaypalExpressSummaryButton: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);
    const onPurchase = async () => {
      setSubmitted(true);
      if (!viewState.paypalInfos) {
        const noInfoError = new Error("No paypal nonce and funding source given");
        (noInfoError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnPurchase;
        purchaseButtonErrors(noInfoError, "Paypal Express Purchase Button", {
          ppNonceExists: false,
        });

        return;
      }
      try {
        await purchase(checkout, viewState.paypalInfos.nonce, "ppl", viewState.paypalInfos.paymentSource);
      } catch (err) {
        (err as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnPurchase;
        purchaseButtonErrors(err as Error, "Paypal Express Purchase Button", {
          ppNonceExists: !!viewState.paypalInfos?.nonce,
        });
      }
    };

    return (
      <Button
        action={onPurchase}
        styles={[ButtonStyles.wide]}
        type={submitted ? ButtonType.disabled : ButtonType.default}
      >
        {submitted ? <Spinner /> : buttonText}
      </Button>
    );
  };

  const PayPalButton: React.FC = () => {
    return (
      <BraintreeButtonLoader
        handleError={purchaseButtonErrors}
        isBlocked={false}
        isExpress={false}
        onReturn={() => undefined}
      />
    );
  };

  /* DEFAULT/INTERNAL PAYMENT */
  const DefaultButton: React.FC = () =>
    paymentModule ? (
      <paymentModule.PurchaseButton
        gotoHandling={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)}
        label={buttonText}
        handleError={purchaseButtonErrors}
      />
    ) : null;

  /* FALLBACK */
  const FallbackButton: React.FC = () => (
    <Button type={ButtonType.disabled} styles={[ButtonStyles.wide]}>
      {t("general.errorOccurred")}
    </Button>
  );

  const purchaseButtonMemoized = React.useMemo(() => {
    let ButtonToReturn = FallbackButton;

    switch (true) {
      case !checkout.cartData.isValid:
        break;
      case paymentId === Payments.PaypalExpressBraintree:
        ButtonToReturn = PaypalExpressSummaryButton;
        break;
      case paymentId === Payments.PaypalBraintree:
        ButtonToReturn = PayPalButton;
        break;
      case paymentId === Payments.GooglePay:
        ButtonToReturn = GooglePayPurchaseButton;
        break;
      case paymentModule !== null:
        ButtonToReturn = DefaultButton;
        break;
    }

    return <ButtonToReturn />;
  }, [paymentId, paymentModule, checkout.cartData.isValid]);

  // endregion

  if (errors.length) {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }
  }

  const isExpressPayment =
    (checkout.paymentId === Payments.PaypalExpressBraintree && !!viewState.paypalInfos) ||
    (checkout.paymentId === Payments.GooglePay && !!viewState.gPayData);

  return (
    <div className={`${styles.wrapper} reduced-width`}>
      <h2>{t("checkout.checkYourOrder")}</h2>
      <CheckoutError>{errors}</CheckoutError>
      <br />
      <br />
      <h2>{t("checkout.yourCart", {count: itemPosCount})}</h2>
      <ItemList
        editable={false}
        goToCartView={() => {
          setIsIntoCheckout(false, checkout);
          updateUrlParams("cart");
        }}
      />

      {isExpressPayment ? (
        <>
          <SummaryAddress />
          <SummaryShippingOption />
          <SummaryPaymentOption />
        </>
      ) : (
        <>
          <SummaryAddress goToAddressView={() => checkoutView.gotoStep(CheckoutStep.ADDRESS)} />
          <SummaryShippingOption goToShippingView={() => checkoutView.gotoStep(CheckoutStep.SHIPPING)} />
          <SummaryPaymentOption goToPaymentView={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)} />
        </>
      )}

      {isCartManipulationAllowed && <VoucherBlock />}
      <AnnotationBlock />
      <SummaryCustomerAccount />
      <SummaryNewsletter />
      <CartDetails />
      {purchaseButtonMemoized}
      <p className={styles.disclaimer}>
        <Trans
          i18nKey="general.disclaimer"
          ns="translations"
          components={{
            a: <a href={t("url.termsAndConditionsLink")} target="_blank" rel="noreferrer" />,
            b: <a href={t("url.privacyLink")} target="_blank" rel="noreferrer" />,
            c: <a href={t("url.withdrawalLink")} target="_blank" rel="noreferrer" />,
          }}
        />
      </p>
    </div>
  );
};

export default React.memo(Summary);

export interface IBraintreeError {
  code: string;
  message: string;
  type: BraintreeErrorTypes;
  details: {
    originalError: {
      code: string;
      message: string;
      type: BraintreeErrorTypes;
      details: {
        originalError?: {
          error: Error;
        };
      };
      error: Error;
    };
  };
}
